import React, { useMemo, useState } from 'react';
import { FiSearch, FiEye, FiX } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
} from '../../components/UI';
import { useFirestoreCollection, usePagination, useUserDirectory } from '../../hooks';
import { cancelVetBooking } from '../../services/adminService';
import styles from './VetBookings.module.css';

// Schema (verified against production documents, 2026-08-21):
//   vetBookings.{ bookingId, userId, vetId, vetOwnerId, vetFirstName,
//                 vetLastName, vetClinicName, vetPhone, vetPhoto, petId,
//                 petName, petType, appointmentDate (Timestamp),
//                 appointmentTime ("14:05"), duration, serviceId, serviceName,
//                 price, paymentStatus, transactionId, status, reasonForVisit,
//                 symptoms, additionalNotes, consultationNotes, prescriptions[],
//                 followUpRequired, followUpDate, contactPhone, isVirtual,
//                 cancellationReason, cancelledAt, createdAt, updatedAt }
// The old code read `scheduledFor`, `customerName`, `vetName` and `reason` —
// none of which exist on these documents, so Scheduled/Reason rendered "-" and
// Customer/Vet rendered raw ids.

/** The appointment slot: a Timestamp date plus a separate "HH:mm" string. */
const bookingWhen = (b) => {
  if (!b) return null;
  const raw = b.appointmentDate || b.scheduledFor;
  if (!raw) return null;
  const d = raw.toDate ? raw.toDate() : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const formatBookingSlot = (b) => {
  const d = bookingWhen(b);
  if (!d) return '-';
  // appointmentDate already carries the chosen time, but appointmentTime is the
  // string the vet's schedule was built from — show it when they disagree.
  const datePart = d.toLocaleDateString();
  const timePart = b.appointmentTime || d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${datePart} ${timePart}`;
};

const vetLabel = (b) =>
  [b?.vetFirstName, b?.vetLastName].filter(Boolean).join(' ').trim() ||
  b?.vetClinicName ||
  b?.vetName ||
  b?.vetId ||
  '-';

// `reasonForVisit` is an optional free-text field the owner rarely fills — it is
// an empty string on 11 of the 12 production bookings. `serviceName` is always
// set (it comes from the vet's service list), so it carries the column when the
// owner wrote nothing, rather than leaving a table of dashes.
// paymentStatus is what the app wrote; only the Stripe webhook sets
// paymentConfirmedServerSide. A "paid" claim the server has not confirmed is
// shown as such, never as paid.
const paymentLabel = (b) => {
  const ps = b?.paymentStatus || '';
  if (!ps) return '';
  if (b?.paymentConfirmedServerSide === true) return `${ps} (confirmed)`;
  if (ps === 'completed' || ps === 'paid') return `${ps} (not confirmed by Stripe)`;
  return ps;
};

// Server flags that need a person to look (functions/index.js
// onVetBookingSlotWrite and the Stripe webhook).
const bookingFlags = (b) => {
  const flags = [];
  if (b?.slotConflict === true) flags.push('Double-booked slot');
  if (b?.slotUnheld === true) flags.push('Slot not reserved');
  if (b?.payerMismatch === true) flags.push('Not written by the payer');
  return flags;
};

const bookingReason = (b) => b?.reasonForVisit || b?.reason || b?.serviceName || '-';

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const VetBookings = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(() => [orderBy('createdAt', 'desc'), limit(500)], []);
  const { data: bookings, loading, error } = useFirestoreCollection('vetBookings', constraints);
  const directory = useUserDirectory();

  const filtered = useMemo(() => {
    let list = bookings;
    if (statusFilter !== 'all') list = list.filter((b) => b.status === statusFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (b) =>
          (b.id || '').toLowerCase().includes(q) ||
          (b.vetId || '').toLowerCase().includes(q) ||
          // Resolved customer name / handle / email, not just the uid.
          directory.searchBlobOf(b.userId).includes(q) ||
          vetLabel(b).toLowerCase().includes(q) ||
          (b.vetClinicName || '').toLowerCase().includes(q) ||
          (b.petName || '').toLowerCase().includes(q) ||
          (b.serviceName || '').toLowerCase().includes(q) ||
          bookingReason(b).toLowerCase().includes(q)
      );
    }
    return list;
  }, [bookings, statusFilter, searchQuery, directory]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  const handleCancel = async (booking) => {
    if (!window.confirm(`Cancel booking ${booking.id}?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await cancelVetBooking({ bookingId: booking.id, reason: 'Cancelled by admin' });
    } catch (err) {
      console.error(err);
      setErrorMsg(`Cancel failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status) => {
    const variants = {
      pending: 'warning',
      confirmed: 'primary',
      completed: 'success',
      cancelled: 'danger',
    };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'unknown'}</Badge>;
  };

  const csvColumns = [
    { key: 'id', label: 'Booking ID' },
    { key: 'customerName', label: 'Customer', accessor: (b) => directory.nameOf(b.userId) },
    { key: 'customerEmail', label: 'Customer Email', accessor: (b) => directory.emailOf(b.userId) },
    { key: 'userId', label: 'Customer UID' },
    { key: 'vetName', label: 'Vet', accessor: (b) => vetLabel(b) },
    { key: 'vetClinicName', label: 'Clinic' },
    { key: 'vetId', label: 'Vet ID' },
    { key: 'petName', label: 'Pet' },
    { key: 'petId', label: 'Pet ID' },
    { key: 'serviceName', label: 'Service' },
    { key: 'price', label: 'Price' },
    { key: 'paymentStatus', label: 'Payment', accessor: (b) => paymentLabel(b) },
    { key: 'status', label: 'Status' },
    { key: 'flags', label: 'Flags', accessor: (b) => bookingFlags(b).join('; ') },
    { key: 'reason', label: 'Reason', accessor: (b) => bookingReason(b) },
    { key: 'scheduledFor', label: 'Scheduled', accessor: (b) => formatBookingSlot(b) },
    { key: 'createdAt', label: 'Booked', accessor: (b) => formatDate(b.createdAt) },
  ];

  const columns = [
    {
      key: 'scheduledFor',
      title: 'Scheduled',
      render: (_, row) => formatBookingSlot(row),
    },
    {
      key: 'customer',
      title: 'Customer',
      render: (_, row) => (
        <div className={styles.stack}>
          <span className={styles.strong}>{directory.nameOf(row.userId) || '-'}</span>
          <span className={styles.meta}>
            {directory.emailOf(row.userId) || directory.handleOf(row.userId) || row.userId || ''}
          </span>
        </div>
      ),
    },
    {
      key: 'pet',
      title: 'Pet',
      render: (_, row) =>
        [row.petName, row.petType].filter(Boolean).join(' · ') || row.petId || '-',
    },
    {
      key: 'vet',
      title: 'Vet',
      render: (_, row) => (
        <div className={styles.stack}>
          <span className={styles.strong}>{vetLabel(row)}</span>
          <span className={styles.meta}>{row.vetClinicName || ''}</span>
        </div>
      ),
    },
    {
      key: 'reason',
      title: 'Reason',
      render: (_, row) => {
        const reason = bookingReason(row);
        return (
          <div className={styles.stack}>
            <span>{reason}</span>
            {/* Only repeat the service when it is not already the reason shown. */}
            {row.serviceName && row.serviceName !== reason && (
              <span className={styles.meta}>{row.serviceName}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      render: (v, row) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {statusBadge(v)}
          {bookingFlags(row).map((f) => (
            <Badge key={f} variant="danger">{f}</Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '120px',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            variant="ghost"
            size="sm"
            icon={<FiEye />}
            onClick={() => {
              setSelected(row);
              setShowDetail(true);
            }}
          />
          {(row.status === 'pending' || row.status === 'confirmed') && (
            <Button
              variant="danger"
              size="sm"
              icon={<FiX />}
              onClick={() => handleCancel(row)}
              disabled={actionLoading}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Vet Bookings</h1>
          {!loading && <Badge variant="primary">{bookings.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          All veterinarian appointments
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {error && (
        <Card>
          <div style={{ color: '#f87171' }}>Error: {error.message}</div>
        </Card>
      )}
      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search by id, customer, vet, reason..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <div className={styles.filters}>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton rows={filtered} columns={csvColumns} filename={`dierend-bookings-${statusFilter}`} />
        </div>

        <Table columns={columns} data={pageData} loading={loading} emptyMessage="No bookings found" />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title="Booking Details" size="lg">
        {selected && (
          <div className={styles.detail}>
            <div className={styles.grid}>
              <div className={styles.item}>
                <span className={styles.label}>Booking ID</span>
                <span className={styles.value}>{selected.id}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Status</span>
                <span className={styles.value}>{statusBadge(selected.status)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Scheduled For</span>
                <span className={styles.value}>{formatBookingSlot(selected)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Duration</span>
                <span className={styles.value}>
                  {selected.duration ? `${selected.duration} min` : '-'}
                  {selected.isVirtual ? ' · video call' : ''}
                </span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Booked</span>
                <span className={styles.value}>{formatDate(selected.createdAt)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Customer</span>
                <span className={styles.value}>{directory.nameOf(selected.userId) || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Customer email</span>
                <span className={styles.value}>{directory.emailOf(selected.userId) || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Contact phone</span>
                <span className={styles.value}>{selected.contactPhone || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Customer UID</span>
                <span className={styles.value}>{selected.userId || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Vet</span>
                <span className={styles.value}>{vetLabel(selected)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Clinic</span>
                <span className={styles.value}>{selected.vetClinicName || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Vet ID</span>
                <span className={styles.value}>{selected.vetId || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Pet</span>
                <span className={styles.value}>
                  {[selected.petName, selected.petType].filter(Boolean).join(' · ') || '-'}
                </span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Pet ID</span>
                <span className={styles.value}>{selected.petId || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Service</span>
                <span className={styles.value}>{selected.serviceName || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Price</span>
                <span className={styles.value}>
                  {selected.price != null ? `€${selected.price}` : '-'}
                  {selected.paymentStatus ? ` · ${paymentLabel(selected)}` : ''}
                </span>
              </div>
              {bookingFlags(selected).length > 0 && (
                <div className={styles.item}>
                  <span className={styles.label}>Needs attention</span>
                  <span className={styles.value}>{bookingFlags(selected).join(' · ')}</span>
                </div>
              )}
              <div className={styles.item}>
                <span className={styles.label}>Reason for visit</span>
                <span className={styles.value}>
                  {selected.reasonForVisit || selected.reason || '(not provided)'}
                </span>
              </div>
            </div>
            {selected.symptoms && (
              <div className={styles.section}>
                <h4>Symptoms</h4>
                <p>{selected.symptoms}</p>
              </div>
            )}
            {(selected.additionalNotes || selected.notes) && (
              <div className={styles.section}>
                <h4>Notes from the owner</h4>
                <p>{selected.additionalNotes || selected.notes}</p>
              </div>
            )}
            {selected.consultationNotes && (
              <div className={styles.section}>
                <h4>Consultation notes</h4>
                <p>{selected.consultationNotes}</p>
              </div>
            )}
            {Array.isArray(selected.prescriptions) && selected.prescriptions.length > 0 && (
              <div className={styles.section}>
                <h4>Prescriptions ({selected.prescriptions.length})</h4>
                <p>
                  {selected.prescriptions
                    .map((rx) =>
                      typeof rx === 'string'
                        ? rx
                        : [rx.name, rx.dosage, rx.frequency].filter(Boolean).join(' · ')
                    )
                    .join(' | ')}
                </p>
              </div>
            )}
            {selected.cancellationReason && (
              <div className={styles.section}>
                <h4>Cancellation</h4>
                <p>
                  {selected.cancellationReason}
                  {selected.cancelledAt ? ` (${formatDate(selected.cancelledAt)})` : ''}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VetBookings;
